// `shortlist.friendly.criteria.tooltip.tpl.html` — the ☰ menu's "Search criteria" panel: the
// alert's criteria as sentences, with when it was created and last changed.
//
// ⚠️ IT IS A TOOLTIP, AND THE WRAP CLASS IS THE SLIM ONE HERE. `_friendly.criteria.tooltip.scss`
// hangs the 190px width off `.friendly-criteria-tooltip-wrap-slim .friendly-criteria-container
// .friendly-criteria-container-slim-view`; the full-view class is 460px, which overflows a
// 200px panel. Both classes are on the element in upstream too — the directive picks the wrap
// from `isSlimView` and the template picks the container class from the same flag.
//
// ⚠️ THE "AND"/"OR" ROW BETWEEN ITEMS IS NOT DECORATION. It renders between — never after —
// the entries of the SKILLS and FIELD_OF_EXPERTISE sections only, and for field-of-expertise
// the word comes from the alert's own `fieldOfExpertiseOperator`. A panel that omits it reads
// as a list of alternatives where the alert means a conjunction.
import type { ReactElement } from 'react'
import { CriteriaFriendlyTextType, type AlertCriteriaData } from '@woo-ext/api'
import { LogicalOperator } from '../search/criteriaOptions.ts'

export interface FriendlyCriteriaPanelProps {
  data?: AlertCriteriaData
  /**
   * `extensionCriteria.fieldOfExpertiseOperator` — `LogicalOperator`, whose values are the
   * ORDINALS 0 (All) and 1 (Any), not the strings their names suggest.
   */
  fieldOfExpertiseOperator?: LogicalOperator | null
  slim?: boolean
  /** `getTimeSince` — the host supplies it, because the locale service is not ported. */
  timeSince: (epoch?: number | null) => string
  onDismiss?: () => void
}

export function FriendlyCriteriaPanel({
  data,
  fieldOfExpertiseOperator,
  slim = true,
  timeSince,
  onDismiss,
}: FriendlyCriteriaPanelProps): ReactElement {
  return (
    <div
      className={`friendly-criteria-container ${
        slim ? 'friendly-criteria-container-slim-view' : 'friendly-criteria-container-full-view'
      }`}
    >
      {data === undefined ? (
        <div>
          <i className="fa fa-spinner fa-spin fa-lg" />
        </div>
      ) : (
        <div>
          <div className="friendly-criteria-header">
            <div className="friendly-criteria-header-container">
              <div className="friendly-criteria-dates">
                <span>Created: {timeSince(data.creationDate)}</span>
                {/* `updateDate ? updateDate : creationDate` — an alert never edited reports
                    its creation date as its last update rather than an empty string. */}
                <span>Last updated: {timeSince(data.updateDate ?? data.creationDate)}</span>
              </div>
            </div>
            <div className="friendly-criteria-x-icon">
              <i
                className="far fa-times fa-lg"
                role="button"
                aria-label="Close"
                tabIndex={0}
                onClick={onDismiss}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return
                  event.preventDefault()
                  onDismiss?.()
                }}
              />
            </div>
          </div>
          <div className="friendly-criteria-content">
            <div className="friendly-criteria-content-container nicer-scroller four-px-scroll">
              {data.criteria.map((section) => (
                <div className="friendly-criteria-content-item" key={`${String(section.type)}-${section.title}`}>
                  <div className="friendly-criteria-section-title">{section.title}</div>
                  <div className="friendly-criteria-section-text-container">
                    {section.text.map((text, index) => (
                      <div key={text}>
                        <div className="friendly-criteria-section-text">{text}</div>
                        {(section.type === CriteriaFriendlyTextType.Skills ||
                          section.type === CriteriaFriendlyTextType.FieldOfExpertise) &&
                        index < section.text.length - 1 ? (
                          <div className="friendly-criteria-logical-operator">
                            <span>
                              <label>
                                {section.type === CriteriaFriendlyTextType.Skills
                                  ? 'AND'
                                  : fieldOfExpertiseOperator === LogicalOperator.All
                                    ? 'AND'
                                    : 'OR'}
                              </label>
                            </span>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
